/**
 * Canonical language registry (frontend) — the ONE place to add a new
 * platform language on the UI side. Mirrors backend utils/languages.js.
 *
 * Adding a language: extend LANGUAGES here, create public/locales/<code>/
 * translation.json (+ fastresearch.json, quickstart.json), register the
 * imports in app/i18n.js, and add a flag in components/FlagIcons.js.
 */

export const LANGUAGES = {
  en: { name: "English", native: "English", supported: true },
  ja: { name: "Japanese", native: "日本語", supported: true },
  de: { name: "German", native: "Deutsch", supported: true },
  fr: { name: "French", native: "Français", supported: true },
  id: { name: "Indonesian", native: "Bahasa Indonesia", supported: true },
  // Legacy locales — partial translations, hidden from pickers
  es: { name: "Spanish", native: "Español", supported: false },
  nl: { name: "Dutch", native: "Nederlands", supported: false }
};

const NAME_TO_CODE = Object.fromEntries(
  Object.entries(LANGUAGES).map(([code, l]) => [l.name, code])
);

/** Codes shown in language pickers: ['en','ja','de','fr','id'] */
export const SUPPORTED_CODES = Object.keys(LANGUAGES).filter(c => LANGUAGES[c].supported);

/** All locale codes with translation files (for i18n supportedLngs) */
export const ALL_CODES = Object.keys(LANGUAGES);

/** Picker entries: [{ code, native }] for supported languages */
export const SUPPORTED_UI_LANGUAGES = SUPPORTED_CODES.map(code => ({
  code,
  name: LANGUAGES[code].native
}));

/** Code OR full name → full English name (what AI endpoints expect). */
export function getLanguageName(codeOrName, fallback = "English") {
  if (!codeOrName) return fallback;
  if (LANGUAGES[codeOrName]) return LANGUAGES[codeOrName].name;
  if (NAME_TO_CODE[codeOrName]) return codeOrName;
  return fallback;
}

/** Full name OR code → ISO code. */
export function getLanguageCode(nameOrCode, fallback = "en") {
  if (!nameOrCode) return fallback;
  if (LANGUAGES[nameOrCode]) return nameOrCode;
  if (NAME_TO_CODE[nameOrCode]) return NAME_TO_CODE[nameOrCode];
  return fallback;
}
